import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * SEO head tags + self-canonical for SPA routes.
 * Props:
 *  - title        string
 *  - description  string
 *  - canonical    absolute OR relative path (we normalize to absolute)
 *  - image        absolute or root-relative path (default: /og.png)
 *  - type         Open Graph type (default: 'website')
 *  - noindex      boolean (adds robots noindex,nofollow)
 */
export default function SEO({
  title = "HashSeal",
  description = "Seal & verify documents with AI + blockchain.",
  canonical,
  image = "/og.png",
  type = "website",
  noindex = false,
}) {
  const { pathname } = useLocation();

  const ORIGIN = "https://hashseal.eu";

  // Helpers
  const normalize = (s) => (s ? s.replace(/\s+/g, " ").trim() : "");
  const clamp = (s, n) => (s && s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);
  const TITLE_MAX = 60;
  const DESC_MAX = 160;

  // Build absolute URLs and strip ?query/#hash from canonical
  const abs = (p) => (String(p).startsWith("http") ? p : `${ORIGIN}${p.startsWith("/") ? p : `/${p}`}`);
  const routeUrl = abs((pathname || "/").replace(/\/+$/, "") || "/");
  const canonicalHref = canonical ? abs(String(canonical).split(/[?#]/)[0]) : routeUrl;

  const imageUrl = image?.startsWith("http") ? image : abs(image || "/og.png");
  const titleOut = clamp(normalize(title), TITLE_MAX);
  const descOut = description ? clamp(normalize(description), DESC_MAX) : "";

  return (
    <Helmet>
      {/* Basic */}
      {titleOut && <title>{titleOut}</title>}
      {descOut && <meta name="description" content={descOut} />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalHref} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      {titleOut && <meta property="og:title" content={titleOut} />}
      {descOut && <meta property="og:description" content={descOut} />}
      <meta property="og:url" content={canonicalHref} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:site_name" content="HashSeal" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {titleOut && <meta name="twitter:title" content={titleOut} />}
      {descOut && <meta name="twitter:description" content={descOut} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
    </Helmet>
  );
}
